import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { Link } from "react-router";
import { styled } from "@pigment-css/react";

export interface BottomNavTab {
  key: string;
  label: string;
  icon: ReactNode;
  /** Filled variant crossfaded in while the tab is active. Falls back to `icon` if omitted. */
  activeIcon?: ReactNode;
  to?: string;
  /** Receives the click before `to` navigates. `preventDefault()` cancels the navigation. */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  isActive?: boolean;
  disabled?: boolean;
}

const TabBarRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  minWidth: 0,
  height: theme.fieldSalesTwo.spacing[72],
  alignItems: "center",
  padding: "3.25px 4.25px",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  border: `1.25px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  background: theme.fieldSalesTwo.colors.surface.glassMedium,
  backdropFilter: "blur(2px)",
  WebkitBackdropFilter: "blur(2px)",
  boxShadow: theme.fieldSalesTwo.shadows.glass,
}));

const Track = styled("div")({
  position: "relative",
  display: "flex",
  flex: 1,
  minWidth: 0,
  height: "100%",
});

const Indicator = styled("span")(({ theme }) => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  zIndex: 0,
  width: "calc(100% / var(--tab-count))",
  transform: "translateX(calc(var(--active-index) * 100%))",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: "rgba(19, 19, 19, 0.04)",
  pointerEvents: "none",
  transition: `transform ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}, opacity ${theme.fieldSalesTwo.motion.duration.sheetOut} ${theme.fieldSalesTwo.motion.easing.standard}`,
  '&[data-visible="false"]': {
    opacity: 0,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: `opacity ${theme.fieldSalesTwo.motion.duration.sheetOut} ${theme.fieldSalesTwo.motion.easing.standard}`,
  },
}));

const TabItem = styled("div")(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  flex: "1 0 0",
  minWidth: 0,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.fieldSalesTwo.spacing[4],
  padding: `14px ${theme.fieldSalesTwo.spacing[24]} 16px`,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  textDecoration: "none",
  color: theme.fieldSalesTwo.colors.neutral[900],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1,
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.wide,
  textAlign: "center",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  transition: "background-color 120ms ease",
  "& [data-icon-filled]": {
    opacity: 0,
  },
  "&[data-active='true'] [data-icon-filled]": {
    opacity: 1,
  },
  "&[data-active='true'] [data-icon-outline]": {
    opacity: 0,
  },
  "&:hover:not([data-active='true'])": {
    background: "rgba(19, 19, 19, 0.04)",
  },
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
    outlineOffset: `-${theme.strokeWidth.regular}`,
  },
  "&:active": {
    background: theme.fieldSalesTwo.colors.neutral[200],
  },
  '&[data-disabled="true"]': {
    opacity: 0.5,
    pointerEvents: "none",
    cursor: "default",
  },
}));

const IconStack = styled("span")({
  display: "grid",
  placeItems: "center",
});

const IconLayer = styled("span")(({ theme }) => ({
  gridArea: "1 / 1",
  display: "flex",
  transition: `opacity ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
}));

interface BottomNavTabBarProps {
  tabs: BottomNavTab[];
}

export function BottomNavTabBar({ tabs }: BottomNavTabBarProps) {
  const activeIndex = tabs.findIndex((tab) => tab.isActive);
  const indicatorStyle = {
    "--tab-count": tabs.length,
    "--active-index": Math.max(activeIndex, 0),
  } as CSSProperties;

  return (
    <TabBarRoot role="tablist" data-bottom-nav-tab-bar="">
      <Track>
        <Indicator aria-hidden="true" data-visible={activeIndex >= 0} style={indicatorStyle} />
        {tabs.map((tab) => {
          const displayIcon = tab.activeIcon ? (
            <IconStack>
              <IconLayer data-icon-outline="">{tab.icon}</IconLayer>
              <IconLayer data-icon-filled="">{tab.activeIcon}</IconLayer>
            </IconStack>
          ) : (
            tab.icon
          );
          const tabContent = (
            <>
              {displayIcon}
              {tab.label}
            </>
          );
          const sharedProps = {
            role: "tab" as const,
            "data-active": Boolean(tab.isActive),
          };

          if (tab.disabled) {
            return (
              <TabItem key={tab.key} {...sharedProps} aria-disabled="true" data-disabled="true">
                {tabContent}
              </TabItem>
            );
          }
          if (tab.to) {
            return (
              <TabItem
                key={tab.key}
                as={Link}
                to={tab.to}
                state={{ direction: "lateral" }}
                onClick={tab.onClick}
                {...sharedProps}
                aria-selected={tab.isActive}
              >
                {tabContent}
              </TabItem>
            );
          }
          if (tab.onClick) {
            return (
              <TabItem
                key={tab.key}
                as="button"
                type="button"
                onClick={tab.onClick}
                {...sharedProps}
                aria-selected={tab.isActive}
              >
                {tabContent}
              </TabItem>
            );
          }
          return (
            <TabItem key={tab.key} {...sharedProps} aria-selected={tab.isActive}>
              {tabContent}
            </TabItem>
          );
        })}
      </Track>
    </TabBarRoot>
  );
}
